import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useRef,
} from 'react';

import { useAuth } from './AuthContext';
import {
  type Workspace,
  fetchWorkspaces as fetchWorkspacesApi,
  createWorkspace as createWorkspaceApi,
  updateWorkspace as updateWorkspaceApi,
  deleteWorkspace as deleteWorkspaceApi,
  getWorkspaceApiErrorMessage,
  type WorkspaceUpdatePayload,
} from '../services/workspaceService';
import { useNavigate } from 'react-router-dom';
import { uiLogger, workspaceLogger } from '../utils/loggerInstance';
import { handleAuthError as handleSharedAuthError } from '../services/authErrorHelper';
import { ChatStoreSynchronizer } from '../components/ChatHistory/ChatStoreSynchronizer';

export type { Workspace };

interface WorkspaceContextType {
  workspaces: Workspace[];
  selectedWorkspace: Workspace | null;
  isLoadingWorkspaces: boolean;
  error: string | null;
  isInitialized: boolean;
  setSelectedWorkspace: (workspace: Workspace | null) => void;
  refreshWorkspaces: () => Promise<Workspace[]>;
  createWorkspace: (name: string, description?: string) => Promise<Workspace | null>;
  updateWorkspace: (workspaceId: string, updates: WorkspaceUpdatePayload) => Promise<Workspace>;
  deleteWorkspace: (workspaceId: string) => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

interface WorkspaceProviderProps {
  children: ReactNode;
}

const SELECTED_WORKSPACE_KEY = 'docufy_selected_workspace';

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const { user, session, loading: authLoading, getBackendUserId } = useAuth();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(() => {
    // Restore selected workspace from localStorage on mount
    try {
      const saved = localStorage.getItem(SELECTED_WORKSPACE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      workspaceLogger.warn('Failed to restore selected workspace from localStorage', { error });
    }
    return null;
  });
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Add refs to prevent duplicate requests
  const workspacesRequestRef = useRef<boolean>(false);
  const workspaceFetchTriggeredRef = useRef<boolean>(false);
  const lastAuthStateRef = useRef<string>('');
  const lastBackendUserIdRef = useRef<string | null>(null);
  // Rate limiting: prevent rapid resets that could cause infinite loops
  const lastResetTimestampRef = useRef<number>(0);
  const RESET_DEBOUNCE_MS = 1000; // Minimum 1 second between resets

  /**
   * Helper function to handle authentication errors (401/403)
   * Clears service worker cache and redirects to login page
   * @param error - The error object from the API call
   * @param context - Context message for logging (e.g., 'fetching workspaces')
   * @returns true if authentication error was handled, false otherwise
   */
  const handleAuthError = useCallback((error: any, context: string): boolean => {
    return handleSharedAuthError({
      error,
      context,
    });
  }, []);

  const syncWorkspaceSelectionStorage = useCallback((workspace: Workspace | null) => {
    if (workspace) {
      try {
        localStorage.setItem(SELECTED_WORKSPACE_KEY, JSON.stringify(workspace));
      } catch (error) {
        workspaceLogger.warn('Failed to persist selected workspace to localStorage', { error });
      }
    } else {
      try {
        localStorage.removeItem(SELECTED_WORKSPACE_KEY);
      } catch (error) {
        workspaceLogger.warn('Failed to remove selected workspace from localStorage', { error });
      }
    }
  }, []);

  const persistSelectedWorkspace = useCallback(
    (workspace: Workspace | null) => {
      setSelectedWorkspace(workspace);
      syncWorkspaceSelectionStorage(workspace);
    },
    [syncWorkspaceSelectionStorage],
  );

  const createWorkspace = useCallback(
    async (name: string, description?: string): Promise<Workspace | null> => {
      if (!user || !session) throw new Error('User must be authenticated to create workspace');

      const internalUserId = getBackendUserId();
      if (!internalUserId) {
        throw new Error('User session not found. Please log in again.');
      }

      try {
        const newWorkspace = await createWorkspaceApi(name, internalUserId, description);

        setWorkspaces((prev) => {
          const updatedWorkspaces = [...prev, newWorkspace];
          if (prev.length === 0) {
            persistSelectedWorkspace(newWorkspace);
          }
          return updatedWorkspaces;
        });

        return newWorkspace;
      } catch (error) {
        uiLogger.error('Failed to create workspace', {
          error,
          workspaceName: name,
          action: 'createWorkspace',
        });
        // Preserve AxiosError (status / response) so UI can map to stable customer-facing copy
        throw error;
      }
    },
    [user, session, getBackendUserId, persistSelectedWorkspace],
  );

  const fetchUserWorkspaces = useCallback(async (): Promise<Workspace[]> => {
    // Prevent duplicate requests
    if (workspacesRequestRef.current || isLoadingWorkspaces) {
      uiLogger.debug('Workspaces fetch already in progress, skipping');
      return workspaces;
    }

    if (!user || !session) {
      setWorkspaces([]);
      return [];
    }

    try {
      workspacesRequestRef.current = true;
      setIsLoadingWorkspaces(true);
      setError(null);

      const userWorkspaces = await fetchWorkspacesApi();

      uiLogger.debug('Fetched workspaces', {
        count: userWorkspaces.length,
        workspaces: userWorkspaces.map((w) => ({ id: w.id, name: w.name })),
      });

      setWorkspaces(userWorkspaces);
      return userWorkspaces;
    } catch (error: any) {
      // Handle authentication errors (401/403)
      if (handleAuthError(error, 'fetching workspaces')) {
        return [];
      }

      uiLogger.error('Error fetching workspaces', {
        error,
        action: 'fetchUserWorkspaces',
      });
      setError('Failed to load workspaces. Please try again.');
      return [];
    } finally {
      setIsLoadingWorkspaces(false);
      workspacesRequestRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, session, navigate, handleAuthError]); // isLoadingWorkspaces intentionally omitted to prevent infinite re-renders

  const updateWorkspace = useCallback(
    async (workspaceId: string, updates: WorkspaceUpdatePayload): Promise<Workspace> => {
      if (!user || !session) {
        throw new Error('User must be authenticated to update a workspace');
      }

      try {
        const updated = await updateWorkspaceApi(workspaceId, updates);
        setWorkspaces((prev) => prev.map((w) => (w.id === workspaceId ? updated : w)));
        setSelectedWorkspace((sel) => {
          if (sel?.id !== workspaceId) {
            return sel;
          }
          try {
            localStorage.setItem(SELECTED_WORKSPACE_KEY, JSON.stringify(updated));
          } catch (error) {
            workspaceLogger.warn('Failed to persist updated workspace to localStorage', { error });
          }
          return updated;
        });
        return updated;
      } catch (error) {
        uiLogger.error('Failed to update workspace', {
          error,
          workspaceId,
          action: 'updateWorkspace',
        });
        throw new Error(
          getWorkspaceApiErrorMessage(error, 'Failed to update workspace. Please try again.'),
        );
      }
    },
    [user, session],
  );

  const deleteWorkspace = useCallback(
    async (workspaceId: string): Promise<void> => {
      if (!user || !session) {
        throw new Error('User must be authenticated to delete a workspace');
      }

      try {
        await deleteWorkspaceApi(workspaceId);
        setWorkspaces((prev) => {
          const next = prev.filter((w) => w.id !== workspaceId);
          setSelectedWorkspace((sel) => {
            if (sel?.id !== workspaceId) {
              return sel;
            }
            const replacement = next.length > 0 ? next[0] : null;
            syncWorkspaceSelectionStorage(replacement);
            return replacement;
          });
          return next;
        });
      } catch (error) {
        uiLogger.error('Failed to delete workspace', {
          error,
          workspaceId,
          action: 'deleteWorkspace',
        });
        throw new Error(
          getWorkspaceApiErrorMessage(error, 'Failed to delete workspace. Please try again.'),
        );
      }
    },
    [user, session, syncWorkspaceSelectionStorage],
  );

  // Validate selected workspace exists in loaded workspaces (including empty list after last delete)
  useEffect(() => {
    if (!selectedWorkspace) {
      return;
    }
    const workspaceExists = workspaces.some((w) => w.id === selectedWorkspace.id);
    if (!workspaceExists) {
      workspaceLogger.warn('Selected workspace no longer exists, clearing selection', {
        workspaceId: selectedWorkspace.id,
        workspaceName: selectedWorkspace.name,
        availableWorkspaces: workspaces.map((w) => ({ id: w.id, name: w.name })),
      });
      persistSelectedWorkspace(null);
    }
  }, [workspaces, selectedWorkspace, persistSelectedWorkspace]);

  // Separate useEffect to handle auto-selection of first workspace
  // Only if no workspace is selected and no persisted workspace exists
  useEffect(() => {
    if (workspaces.length > 0 && !selectedWorkspace) {
      // Check if persisted workspace exists in the loaded workspaces
      try {
        const persisted = localStorage.getItem(SELECTED_WORKSPACE_KEY);
        if (persisted) {
          const persistedWorkspace = JSON.parse(persisted);
          const foundWorkspace = workspaces.find((w) => w.id === persistedWorkspace.id);
          if (foundWorkspace) {
            persistSelectedWorkspace(foundWorkspace);
            return;
          }
        }
      } catch (error) {
        workspaceLogger.warn('Failed to restore persisted workspace', { error });
      }
      // If no persisted workspace found, select the first one
      persistSelectedWorkspace(workspaces[0]);
    }
  }, [workspaces, selectedWorkspace, persistSelectedWorkspace]);

  /**
   * Checks if auth state has changed and updates the ref
   */
  const checkAndUpdateAuthState = useCallback((): boolean => {
    const currentAuthState = `${user?.id}-${session?.access_token ? 'has-token' : 'no-token'}-${authLoading}`;
    const hasChanged = currentAuthState !== lastAuthStateRef.current;

    if (hasChanged) {
      lastAuthStateRef.current = currentAuthState;
    }

    return hasChanged;
  }, [user?.id, session?.access_token, authLoading]);

  /**
   * Handles workspace fetching when user is authenticated
   */
  const handleAuthenticatedUser = useCallback(() => {
    if (!user || !session) return;

    uiLogger.debug('Auth changed - fetching workspaces for user', { userId: user.id });

    const internalUserId = getBackendUserId();
    uiLogger.debug('Auth state changed - user and session available', {
      userId: user.id,
      hasInternalUserId: !!internalUserId,
    });

    // IMPORTANT: If we're already on the main app (chat), don't trigger any redirects
    const currentPath = globalThis.location.pathname;
    if (currentPath === '/chat') {
      uiLogger.debug('Already on chat, skipping any potential redirects', {
        action: 'useEffect[authLoading]',
      });
    }

    if (internalUserId && !workspaceFetchTriggeredRef.current) {
      uiLogger.debug('WorkspaceContext: Fetching workspaces with internal user ID', {
        internalUserId,
      });
      workspaceFetchTriggeredRef.current = true;
      lastBackendUserIdRef.current = internalUserId;
      fetchUserWorkspaces();
    } else if (internalUserId) {
      uiLogger.debug('WorkspaceContext: Workspace fetch already triggered, skipping');
    } else {
      uiLogger.debug('WorkspaceContext: No internal user ID found, skipping workspace fetch');
      // Only reset the trigger ref if enough time has passed since last reset to prevent infinite loops
      // This prevents rapid oscillations between null and a value from causing retry loops
      const now = Date.now();
      const timeSinceLastReset = now - lastResetTimestampRef.current;

      if (timeSinceLastReset >= RESET_DEBOUNCE_MS) {
        uiLogger.debug('WorkspaceContext: Resetting trigger ref after debounce period', {
          timeSinceLastReset,
        });
        workspaceFetchTriggeredRef.current = false;
        lastBackendUserIdRef.current = null;
        lastResetTimestampRef.current = now;
      } else {
        uiLogger.debug('WorkspaceContext: Skipping reset due to debounce', {
          timeSinceLastReset,
          remainingMs: RESET_DEBOUNCE_MS - timeSinceLastReset,
        });
      }
    }

    setIsInitialized(true);
  }, [user, session, getBackendUserId, fetchUserWorkspaces]);

  /**
   * Clears workspace state when user is not authenticated
   */
  const clearWorkspaceState = useCallback(() => {
    setWorkspaces([]);
    persistSelectedWorkspace(null);
    setError(null);
    setIsInitialized(true);
    workspaceFetchTriggeredRef.current = false;
    lastBackendUserIdRef.current = null;
    lastResetTimestampRef.current = Date.now();
  }, [persistSelectedWorkspace]);

  // Single useEffect to handle workspace fetching based on auth state
  useEffect(() => {
    const authStateChanged = checkAndUpdateAuthState();

    uiLogger.debug('Auth useEffect triggered', {
      authLoading,
      hasUser: !!user,
      hasSession: !!session,
      userId: user?.id,
      authStateChanged,
    });

    if (!authStateChanged) {
      uiLogger.debug('Auth state unchanged, skipping');
      return;
    }

    if (authLoading) {
      return;
    }

    if (user && session) {
      handleAuthenticatedUser();
    } else {
      clearWorkspaceState();
    }
  }, [
    user,
    session,
    authLoading,
    checkAndUpdateAuthState,
    handleAuthenticatedUser,
    clearWorkspaceState,
  ]);

  // Watch for backendUserId changes and retry workspace fetch if it becomes available
  // This is especially important for B2B users where backendUserId might not be available initially
  useEffect(() => {
    if (!user || !session || authLoading) {
      return;
    }

    const currentBackendUserId = getBackendUserId();
    const previousBackendUserId = lastBackendUserIdRef.current;

    // If backendUserId changed from null/undefined to a value, and we haven't successfully fetched workspaces yet
    if (
      currentBackendUserId &&
      currentBackendUserId !== previousBackendUserId &&
      !workspaceFetchTriggeredRef.current &&
      !isLoadingWorkspaces &&
      workspaces.length === 0
    ) {
      uiLogger.debug('WorkspaceContext: Backend user ID became available, fetching workspaces', {
        backendUserId: currentBackendUserId,
        previousBackendUserId,
      });
      workspaceFetchTriggeredRef.current = true;
      lastBackendUserIdRef.current = currentBackendUserId;
      fetchUserWorkspaces();
    } else if (currentBackendUserId && currentBackendUserId !== previousBackendUserId) {
      // Update the ref even if we don't fetch (e.g., if workspaces already loaded)
      lastBackendUserIdRef.current = currentBackendUserId;
    }
  }, [
    user,
    session,
    authLoading,
    getBackendUserId,
    workspaces.length,
    isLoadingWorkspaces,
    fetchUserWorkspaces,
  ]);

  const refreshWorkspaces = useCallback(async (): Promise<Workspace[]> => {
    // Reset the trigger to allow refetch even if it was already triggered
    workspaceFetchTriggeredRef.current = false;
    const result = await fetchUserWorkspaces();
    return result;
  }, [fetchUserWorkspaces]);

  const value: WorkspaceContextType = {
    workspaces,
    selectedWorkspace,
    isLoadingWorkspaces,
    error,
    isInitialized,
    setSelectedWorkspace: persistSelectedWorkspace,
    refreshWorkspaces,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      <ChatStoreSynchronizer selectedWorkspaceId={selectedWorkspace?.id ?? null} />
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextType {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}

/** Non-throwing variant for callers that may remount during Vite HMR without a matching provider instance. */
export function useOptionalWorkspace(): WorkspaceContextType | undefined {
  return useContext(WorkspaceContext);
}
